/* Pull down to refresh: drag the page down from the very top (phone or tablet, in Safari and in the home-screen app,
   which has no pull-to-refresh of its own) and let go to refresh from the server. Not inside open windows, pick-lists, the full-screen timer
   or while typing. The browser's own pull-to-reload is switched off in the CSS (overscroll-behavior), so this is the
   only one, and it never reloads the whole page unless a newer version of the app is waiting. */
const NEED = 70;   // how far (in px, after resistance) to pull before letting go refreshes

export function installPullToRefresh(refresh: () => Promise<string>) {
  const bar = document.createElement('div');
  bar.className = 'ptr'; bar.setAttribute('aria-live', 'polite');
  bar.innerHTML = '<span class="ptr-ic">↓</span><span class="ptr-t">Pull to refresh</span>';
  document.body.prepend(bar);
  const ic = bar.querySelector<HTMLElement>('.ptr-ic')!, txt = bar.querySelector<HTMLElement>('.ptr-t')!;
  let y0: number | null = null, x0 = 0, pull = 0, busy = false;
  const blocked = (t: EventTarget | null) => !!document.querySelector('#modalRoot .modal-backdrop, .combo-panel, #fsRoot .fs-overlay') ||
    !!(t as HTMLElement | null)?.closest?.('input, textarea, select, .modal, .combo-panel, .sug');
  const place = (d: number, anim = false) => { bar.style.transition = anim ? 'transform .2s ease' : 'none'; bar.style.transform = `translate(-50%, ${d - 56}px)`; };
  const show = (state: 'pull' | 'ready' | 'busy' | 'done', text: string) => {
    bar.dataset.state = state; txt.textContent = text; ic.textContent = state === 'busy' ? '↻' : state === 'done' ? '✓' : state === 'ready' ? '↑' : '↓';
  };
  const reset = () => { y0 = null; pull = 0; place(0, true); };
  addEventListener('touchstart', e => {
    if (busy || e.touches.length !== 1 || window.scrollY > 0 || blocked(e.target)) { y0 = null; return; }
    y0 = e.touches[0].clientY; x0 = e.touches[0].clientX; pull = 0;
  }, {passive: true});
  addEventListener('touchmove', e => {
    if (y0 === null) return;
    const dy = e.touches[0].clientY - y0, dx = e.touches[0].clientX - x0;
    if (!pull && Math.abs(dx) > 4 && Math.abs(dx) > Math.abs(dy)) { y0 = null; return; }   // sideways first: a row being swiped, not a pull
    if (!pull && dy > 0 && Math.abs(dx) >= dy) return;                                       // not clear yet
    if (dy <= 0 || window.scrollY > 0) { if (pull) reset(); else y0 = null; return; }
    if (e.cancelable) e.preventDefault();             // this is a pull, not a scroll
    pull = Math.min(110, dy * 0.5); place(pull);
    show(pull >= NEED ? 'ready' : 'pull', pull >= NEED ? 'Release to refresh' : 'Pull to refresh');
  }, {passive: false});
  const end = async () => {
    if (y0 === null) return;
    const go = pull >= NEED; y0 = null;
    if (!go) { reset(); return; }
    busy = true; place(64, true); show('busy', 'Refreshing…');
    let msg = 'Up to date';
    try { msg = await refresh(); } catch { msg = 'Could not refresh'; }
    show('done', msg);
    setTimeout(() => { place(0, true); busy = false; }, 1100);
  };
  addEventListener('touchend', () => void end());
  addEventListener('touchcancel', () => { if (!busy) reset(); });
}
